import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { templateListQuery } from '@xdocs/schemas';
import type { DocumentKind, ErrorAlert, Template, TemplateListResult } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'name', dir: 'asc' };

export interface UseTemplatesListResult {
  rows: Template[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
}

/**
 * F1 `/templates` "Start faster" gallery — server-side pagination over
 * `template.list`. Mirrors `apps/web/src/routes/personas/usePersonasList.ts`
 * exactly, minus the `scope` (`mine`/`public`) param/tab-reset effect:
 * `templates` has no `ownerId` (`packages/db/src/schema/documents.ts`), so
 * there's no scope to enforce — only the optional `kind` filter, which gets
 * the same "reset accumulated rows on change" treatment `usePersonasList`
 * gives its scope tabs.
 */
export function useTemplatesList(kind: DocumentKind | undefined): UseTemplatesListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<Template[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = useMemo(() => ['templates', 'list', kind, query] as const, [kind, query]);

  const result = useQuery<TemplateListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'template.list', templateListQuery.parse({ ...query, kind })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  // Reset accumulated rows when the `kind` filter changes so a previous
  // filter's rows never bleed into a freshly-selected filter's first page
  // (mirrors `usePersonasList`'s scope-switch reset, same rationale).
  useEffect(() => {
    setRows([]);
    appendModeRef.current = false;
    setQuery(DEFAULT_QUERY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const invalidate = useCallback(() => void queryClient.invalidateQueries({ queryKey: ['templates', 'list'] }), [queryClient]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry: () => void result.refetch(),
    invalidate,
  };
}
